import React, { FC } from 'react';
import ToolTip from 'components/ToolTip/ToolTip';
import { setUser } from 'redux/slices';
import { useChangeThemeMutation } from 'redux/apiSlices/user.slices';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { ReactComponent as SunIcon } from 'assets/images/dashbarIcons/sun.svg';
import { ReactComponent as MoonIcon } from 'assets/images/dashbarIcons/moon.svg';
import SThemeToggle from './ThemeToggle.style';

/* The theme lives on the user, so the switch persists rather than resetting on
   the next login. Layout already mirrors `user.theme` into the store, so this
   only has to save it — the repaint follows from there and no second listener
   is needed. */
const ThemeToggle: FC = () => {
  const dispatch = useAppDispatch();
  const theme = useAppSelector(state => state.appSettings.theme);
  const user = useAppSelector(state => state.authSlice.user);
  const [changeTheme, { isLoading }] = useChangeThemeMutation();

  const next = theme === 'dark' ? 'light' : 'dark';

  const onToggle = async () => {
    if (!user?.id) return;
    await changeTheme({
      resourceId: user.id,
      theme: next.toUpperCase(),
    }).unwrap();
    dispatch(setUser({ ...user, theme: next }));
  };

  /* The button shows where it takes you, not where you are: the design draws a
     crescent on the light bar (node 12:1309), so the sun belongs on the dark
     one. */
  const Glyph = theme === 'dark' ? SunIcon : MoonIcon;
  const label = `Switch to ${next} mode`;

  return (
    <ToolTip title={label}>
      <SThemeToggle
        type="button"
        onClick={onToggle}
        disabled={isLoading || !user?.id}
        aria-label={label}
      >
        <Glyph width={16} height={16} aria-hidden />
      </SThemeToggle>
    </ToolTip>
  );
};

export default ThemeToggle;
